import React from "react";
import { Route } from "react-router-dom";
import PropTypes from "prop-types";
import { sessionManager } from "@authentication/services/SessionManager";
import { authenticationService } from "@authentication/services/AuthenticationService";
import { UnAuthorized } from "@authentication/components/UnAuthorized";
import { Forbidden } from "@components/HttpErrors";

const getUserRole = (user) => {
  if (!user?.id) return "anonymous";
  if (!user.roles?.length) return "patron";

  try {
    if (authenticationService.hasRoles(user, ["admin"])) return "admin";
    if (authenticationService.hasRoles(user, ["librarian"])) return "librarian";
    return "patron";
  } catch (error) {
    if (user.roles && Array.isArray(user.roles)) {
      const roleNames = user.roles
        .map((role) => (typeof role === "string" ? role : role.name))
        .filter(Boolean);

      if (roleNames.includes("admin")) return "admin";
      if (roleNames.includes("librarian")) return "librarian";
    }
    return "patron";
  }
};

export const checkRouteAccess = (currentPath, user = null) => {
  if (!currentPath) return { allowed: true };

  const userRole = getUserRole(user);
  const isAuthenticated = !!user?.id;

  const publicRoutes = [
    "/login",
    "/signup",
    "/logout",
    "/forgot-password",
    "/reset-password",
    "/confirm-email",
    "/about",
    "/contact",
    "/library-policy",
    "/opening-hours",
    "/faqs",
    "/attendance",
    "/error",
  ];

  const opacRoutes = [
    "/",
    "/search",
    "/literature",
    "/series",
    "/request",
    "/slip-form",
    "/selfcheckout",
  ];

  const librarianRoutes = [
    "/backoffice/acquisition",
    "/backoffice/checkin",
    "/backoffice/checkout",
    "/backoffice/documents",
    "/backoffice/document-requests",
    "/backoffice/document-usage",
    "/backoffice/eitems",
    "/backoffice/items",
    "/backoffice/loans",
    "/backoffice/locations",
    "/backoffice/internal-locations",
    "/backoffice/patrons",
    "/backoffice/series",
    "/backoffice/specialCollection",
    "/backoffice/stats",
    "/backoffice/fee-management",
    "/backoffice/documentLabels",
    "/backoffice/weeded-documents",
    "/backoffice/attendanceSummary",
  ];

  const isOpacRoute = opacRoutes.some(
    (route) => currentPath === route || currentPath.startsWith(`${route}/`)
  );
  const isPublicRoute = publicRoutes.includes(currentPath);
  const isAuthenticatedRoute = ["/profile", "/dashboard"].includes(currentPath);
  const isLibrarianRoute = librarianRoutes.some((route) =>
    currentPath.startsWith(route)
  );
  const isAdminOnlyRoute =
    currentPath.startsWith("/backoffice") &&
    (currentPath === "/backoffice" ||
      currentPath.startsWith("/backoffice/auditLogs"));

  if (userRole === "admin") {
    if (isAdminOnlyRoute || isAuthenticatedRoute || isPublicRoute) {
      return { allowed: true };
    }

    if (
      isOpacRoute ||
      (currentPath.startsWith("/backoffice") && !isAdminOnlyRoute)
    ) {
      return {
        allowed: false,
        reason: "admin_blocked",
        message:
          "Administrators cannot access this resource. This page is reserved for library staff and patrons.",
      };
    }

    return { allowed: true };
  }

  if (userRole === "librarian") {
    if (isLibrarianRoute || isOpacRoute || isPublicRoute) {
      return { allowed: true };
    }

    if (isAuthenticatedRoute) {
      return { allowed: true };
    }

    if (currentPath.startsWith("/backoffice") && !isLibrarianRoute) {
      return {
        allowed: false,
        reason: "insufficient_privileges",
        message:
          "You do not have sufficient privileges to access this resource.",
      };
    }

    return { allowed: true };
  }

  if (userRole === "patron") {
    if (isAuthenticatedRoute && !isAuthenticated) {
      return { allowed: false, reason: "authentication_required" };
    }

    if (
      isOpacRoute ||
      isPublicRoute ||
      (isAuthenticatedRoute && isAuthenticated)
    ) {
      return { allowed: true };
    }

    return { allowed: true };
  }

  if (userRole === "anonymous") {
    if (isOpacRoute || isPublicRoute) {
      return { allowed: true };
    }

    if (isAuthenticatedRoute || currentPath.startsWith("/backoffice")) {
      return {
        allowed: false,
        reason: currentPath.startsWith("/backoffice")
          ? "authentication_required"
          : "authentication_required",
      };
    }

    return { allowed: true };
  }

  return { allowed: true };
};

const LoadingComponent = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      fontSize: "18px",
    }}
  >
    Loading...
  </div>
);

export const ProtectedRoute = (props) => {
  const {
    path,
    exact = false,
    component: Component,
    children,
    render,
    customUnauthorizedComponent = null,
    customForbiddenComponent = null,
    customLoadingComponent = null,
    ...rest
  } = props;

  const [user, setUser] = React.useState(sessionManager.user);

  React.useEffect(() => {
    let mounted = true;
    const pollInterval = setInterval(() => {
      if (!mounted) return;
      const currentUser = sessionManager.user;
      if (currentUser !== user) {
        setUser(currentUser);
      }
    }, 1000);

    return () => {
      mounted = false;
      clearInterval(pollInterval);
    };
  }, [user]);

  return (
    <Route
      path={path}
      exact={exact}
      {...rest}
      render={(routeProps) => {
        if (user === undefined) {
          return customLoadingComponent || <LoadingComponent />;
        }

        const accessCheck = checkRouteAccess(
          routeProps.location.pathname,
          user
        );

        if (!accessCheck.allowed) {
          if (accessCheck.reason === "authentication_required") {
            return customUnauthorizedComponent || <UnAuthorized />;
          }

          if (
            ["admin_blocked", "insufficient_privileges"].includes(
              accessCheck.reason
            )
          ) {
            if (customForbiddenComponent) {
              return React.createElement(customForbiddenComponent, {
                user,
                message: accessCheck.message,
              });
            }
            return <Forbidden message={accessCheck.message} />;
          }
        }

        if (Component) return <Component {...routeProps} />;
        if (render) return render(routeProps);
        return children;
      }}
    />
  );
};

ProtectedRoute.propTypes = {
  path: PropTypes.string,
  exact: PropTypes.bool,
  component: PropTypes.elementType,
  children: PropTypes.node,
  render: PropTypes.func,
  customUnauthorizedComponent: PropTypes.elementType,
  customForbiddenComponent: PropTypes.elementType,
  customLoadingComponent: PropTypes.elementType,
};

ProtectedRoute.defaultProps = {
  path: undefined,
  exact: false,
  component: undefined,
  children: undefined,
  render: undefined,
  customUnauthorizedComponent: null,
  customForbiddenComponent: null,
  customLoadingComponent: null,
};

export const withRouteProtection = (WrappedComponent) => {
  const RouteProtectedComponent = (props) => {
    const [user, setUser] = React.useState(sessionManager.user);

    React.useEffect(() => {
      let mounted = true;
      const pollInterval = setInterval(() => {
        if (!mounted) return;
        const currentUser = sessionManager.user;
        if (currentUser !== user) {
          setUser(currentUser);
        }
      }, 1000);

      return () => {
        mounted = false;
        clearInterval(pollInterval);
      };
    }, [user]);

    if (user === undefined) {
      return <LoadingComponent />;
    }

    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : "/";
    const accessCheck = checkRouteAccess(currentPath, user);

    if (!accessCheck.allowed) {
      if (accessCheck.reason === "authentication_required") {
        return <UnAuthorized />;
      }

      if (
        ["admin_blocked", "insufficient_privileges"].includes(
          accessCheck.reason
        )
      ) {
        return <Forbidden message={accessCheck.message} />;
      }
    }

    return <WrappedComponent {...props} />;
  };

  RouteProtectedComponent.displayName = `withRouteProtection(${
    WrappedComponent.displayName || WrappedComponent.name || "Component"
  })`;

  return RouteProtectedComponent;
};

export const useRouteAccess = (path = null) => {
  const user = sessionManager.user;
  const currentPath =
    path || (typeof window !== "undefined" ? window.location.pathname : "/");
  return checkRouteAccess(currentPath, user);
};
